import React, { useState, useCallback } from 'react';
import { css } from '@emotion/react';

const navWrapper = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 96px;
  padding: 0 40px;
  background: #fff;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  position: relative;
  z-index: 10;

  @media (max-width: 1169px) {
    height: 56px;
    padding: 0 24px;
  }
`;

const logoStyles = css`
  width: 160px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 80px;

    svg {
      height: 10px;
    }
  }
`;

const navItems = css`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 1169px) {
    display: none;
  }
`;

const navDropdown = css`
  display: none;

  @media (max-width: 1169px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    padding: 0 8px;
    border-radius: 6px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: 'FlodeskSans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #10100f;
    white-space: nowrap;
  }
`;

const navLink = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.16px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const navLinkActive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.16px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  color: #121212;
`;

const divider = css`
  width: 1px;
  height: 16px;
  background: rgba(55, 49, 47, 0.12);
`;

const profileSection = css`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  width: 160px;
  flex-shrink: 0;
  cursor: pointer;

  @media (max-width: 767px) {
    width: 80px;
  }
`;

const profileAvatar = css`
  width: 40px;
  height: 40px;
  border-radius: 60px;
  border: 1px solid rgba(55, 49, 47, 0.12);
  object-fit: cover;

  @media (max-width: 767px) {
    width: 32px;
    height: 32px;
  }
`;

const chevronDown = css`
  width: 10px;
  height: 14px;
  color: rgba(61, 46, 41, 0.52);

  @media (max-width: 1169px) {
    display: none;
  }
`;

const dropdownChevron = css`
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
`;

const overlay = css`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 80px;
`;

const closeButton = css`
  position: absolute;
  top: 28px;
  right: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const overlayNav = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const overlayLink = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 30px;
  line-height: 34.03px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const overlayLinkActive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 30px;
  line-height: 34.03px;
  color: #10100f;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
`;

export default function TopNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDivider, setShowDivider] = useState(true);

  const openMenu = useCallback(() => setIsMenuOpen(true), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleDivider = useCallback(() => setShowDivider(prev => !prev), []);

  return (
    <>
      <nav css={navWrapper}>
        <div css={logoStyles}>
          <svg height="14" viewBox="0 0 57 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.3188 3.02031C16.9384 3.02031 18.8421 4.93639 18.8421 7.51821C18.8421 10.1001 16.9384 12 14.3188 12C11.6667 12 9.81182 10.1001 9.81182 7.51821C9.81182 4.93639 11.6667 3.02031 14.3188 3.02031ZM14.3188 4.74153C12.7568 4.74153 11.748 5.92696 11.748 7.502C11.748 9.12584 12.7568 10.295 14.3188 10.295C15.8971 10.295 16.8733 9.10952 16.8733 7.502C16.8733 5.94317 15.8808 4.74153 14.3188 4.74153Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M35.1292 3.02031C37.944 3.02031 39.4735 5.08255 39.4735 7.59945C39.4735 7.7131 39.4735 7.92421 39.4573 8.08661H32.6235C32.6886 9.4993 33.8601 10.3599 35.2268 10.3599C36.1542 10.3599 36.9353 9.9377 37.4234 9.12586L39.3108 9.46685C38.7739 11.0906 36.984 11.9837 35.1455 11.9837C32.5096 11.9837 30.6873 10.1326 30.6873 7.51821C30.6873 4.96891 32.5259 3.02033 35.1292 3.02031ZM35.1292 4.61166C33.795 4.61167 32.8512 5.43991 32.656 6.60902H37.4559C37.2769 5.35866 36.4471 4.61166 35.1292 4.61166Z"
              fill="#111110"
            />
            <path
              d="M44.0942 2.9878C45.9002 2.9878 47.1695 3.97828 47.2996 5.55337L45.461 5.6184C45.3146 4.98506 44.8101 4.53042 43.9966 4.53042C43.1994 4.53042 42.7275 4.9201 42.7275 5.47213C42.7275 5.92685 43.0041 6.13794 43.6875 6.3653L45.3308 6.9336C46.6975 7.40454 47.4949 7.9566 47.4949 9.25564C47.4949 11.0256 46.0467 11.9836 44.0942 11.9836C42.1254 11.9836 40.7749 11.0256 40.6448 9.33679L42.581 9.25564C42.6299 9.88892 43.1505 10.4735 44.1105 10.4735C45.0054 10.4735 45.4934 10.0513 45.4934 9.46676C45.4934 8.89844 45.1193 8.70355 44.2081 8.39508L43.0041 8.00528C41.6211 7.56688 40.775 6.90115 40.775 5.63461C40.775 4.0433 42.1091 2.9878 44.0942 2.9878Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M28.9135 11.7721H27.1074V11.155C27.1074 10.9276 27.1074 10.749 27.14 10.6029H27.0749C26.489 11.4635 25.4478 11.9669 24.2112 11.9669C21.7706 11.9669 20.0947 10.0833 20.0947 7.53396C20.0947 4.98458 21.7705 3.03599 24.1298 3.03596C25.3827 3.03596 26.4241 3.57189 26.9772 4.35136H27.0261C27.0098 3.45824 26.9935 3.23089 26.9935 2.22413V0.0481693H28.9135V11.7721ZM24.5691 4.77349C23.0072 4.7735 22.0309 6.00759 22.0309 7.51766C22.0309 9.06033 23.0234 10.2781 24.5691 10.2781C26.0986 10.2781 27.1074 9.07655 27.1074 7.50145C27.1074 5.95883 26.0986 4.77349 24.5691 4.77349Z"
              fill="#111110"
            />
            <path
              d="M4.03511 0C4.21413 0 4.39312 4.0185e-05 4.60462 0.0325143V1.70501C4.53955 1.68875 4.49066 1.6888 4.40935 1.6888C3.69348 1.68881 3.22165 1.93235 3.12406 2.6143C3.10776 2.76049 3.10776 2.85791 3.10776 2.98789V3.23142H4.60462V4.85519H3.12406V11.7726H1.20405V4.85519H0V3.23142H1.20405V2.93916C1.20405 1.10429 2.03387 1.08386e-05 4.03511 0Z"
              fill="#111110"
            />
            <path d="M8.03733 11.772H6.08481V0.0481693H8.03733V11.772Z" fill="#111110" />
            <path
              d="M51.1215 2.67868C51.1215 4.10764 51.089 5.00074 51.0727 6.26729L51.089 6.2836C51.691 5.63411 52.2442 5.14693 52.8624 4.51365L54.1153 3.23087H56.7675L52.2279 7.25782L56.9139 11.772H54.1642C53.0903 10.7003 52.0489 9.57995 51.1053 8.52449H51.089C51.1053 9.23892 51.1215 9.82351 51.1215 10.6353V11.772H49.2179V0.0481693H51.1215V2.67868Z"
              fill="#111110"
            />
          </svg>
        </div>

        <div css={navItems}>
          <span css={navLinkActive}>Emails</span>
          <span css={navLink}>Forms</span>
          <span css={navLink}>Workflows</span>
          <span
            css={navLink}
            role="button"
            tabIndex={0}
            onClick={toggleDivider}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleDivider();
            }}
          >
            Checkouts
          </span>
          {showDivider && <div css={divider} />}
          <span css={navLink}>Products</span>
          <span css={navLink}>Audience</span>
          <span css={navLink}>Analytics</span>
        </div>

        <button css={navDropdown} type="button" onClick={openMenu}>
          Emails
          <svg css={dropdownChevron} width="10" height="18" viewBox="0 0 10 18" fill="none">
            <path
              d="M1 7L5 11L9 7"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div css={profileSection}>
          <img css={profileAvatar} src="/images/profile.png" alt="Profile" />
          <svg css={chevronDown} viewBox="0 0 10 14" fill="none">
            <path
              d="M1 5L5 9L9 5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </nav>

      {isMenuOpen && (
        <div css={overlay} onClick={closeMenu} role="presentation">
          <button css={closeButton} type="button" onClick={closeMenu} aria-label="Close menu">
            <svg width="19" height="19" viewBox="0 0 19 19" fill="none">
              <path
                d="M1 1L18 18M18 1L1 18"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div css={overlayNav}>
            <span css={overlayLinkActive}>Emails</span>
            <span css={overlayLink}>Forms</span>
            <span css={overlayLink}>Workflows</span>
            <span css={overlayLink}>Checkouts</span>
            <span css={overlayLink}>Products</span>
            <span css={overlayLink}>Audience</span>
            <span css={overlayLink}>Analytics</span>
          </div>
        </div>
      )}
    </>
  );
}
